import React, { useState, useEffect } from 'react';
import { useSubscription } from './subscriptioncontext';
import './upgrade.css';

function UpgradeModal({ isOpen, onClose, onSuccess, feature, trialInfo }) {
    const { getSubscriptionPlans, upgradeSubscription } = useSubscription();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [upgrading, setUpgrading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadPlans();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const loadPlans = async () => {
        try {
            setLoading(true);
            const allPlans = await getSubscriptionPlans();
            // Filter out trial plans for the upgrade modal
            const paidPlans = allPlans.filter(plan => !plan.is_trial);
            setPlans(paidPlans);
        } catch (error) {
            console.error('Error loading plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = async (planId) => {
        try {
            setUpgrading(true);
            setSelectedPlan(planId);
            
            const result = await upgradeSubscription(planId);
            
            if (result.success) {
                onSuccess();
            } else {
                // For now, just show the payment integration message
                alert(result.message || 'Payment integration coming soon!');
                onClose();
            }
        } catch (error) {
            console.error('Error upgrading subscription:', error);
            alert('Failed to upgrade subscription. Please try again.');
        } finally {
            setUpgrading(false);
            setSelectedPlan(null);
        }
    };

    const getFeatureDisplayName = (feature) => {
        const featureNames = {
            'recipe_engine': 'Recipe Engine',
            
            'ai_usage': 'AI Features',
            'trial_upgrade': 'Premium Features'
        };
        return featureNames[feature] || 'Premium Feature';
    };

    const formatPrice = (price, currency) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0
        }).format(price);
    };

    if (!isOpen) return null;

    return (
        <div className="upgrade-modal-overlay" onClick={onClose}>
            <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
                <div className="upgrade-modal-header">
                    <h2>Upgrade Your Plan</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="upgrade-modal-content">
                    {/* Feature context */}
                    <div className="upgrade-context">
                        <div className="context-text">
                            <h3>Unlock {getFeatureDisplayName(feature)}</h3>
                            {trialInfo && (
                                <p>
                                    You've used {trialInfo.usage_count} of {trialInfo.usage_limit} free trials.
                                    Upgrade to continue enjoying all features!
                                </p>
                            )}
                            {!trialInfo && (
                                <p>This feature requires a paid subscription to access.</p>
                            )}
                        </div>
                    </div>

                    {/* Plans */}
                    {loading ? (
                        <div className="upgrade-plans-loading">
                            <div className="upgrade-loading-spinner"></div>
                            <p>Loading subscription plans...</p>
                        </div>
                    ) : (
                        <div className="upgrade-subscription-plans">
                            
                            <div className="upgrade-plans-grid">
                                {plans.map(plan => (
                                    <div 
                                        key={plan.id} 
                                        className={`upgrade-plan-card ${plan.name === 'premium' ? 'recommended' : ''}`}
                                    >
                                        {plan.name === 'premium' && (
                                            <div className="upgrade-recommended-badge">Recommended</div>
                                        )}
                                        
                                        <div className="upgrade-plan-header">
                                            <h4>{plan.display_name}</h4>
                                            <div className="upgrade-plan-price">
                                                {formatPrice(plan.price, plan.currency)}
                                                <span className="upgrade-plan-period">/month</span>
                                            </div>
                                        </div>

                                        <div className="upgrade-plan-features">
                                            <ul>
                                                {plan.features.recipe_engine_access && (
                                                    <li>Recipe Engine Access</li>
                                                )}
                                                <li>Cookbook Access (Always Available)</li>
                                                {plan.features.ai_usage_limit === 0 ? (
                                                    <li>Unlimited AI Features</li>
                                                ) : (
                                                    <li>{plan.features.ai_usage_limit} AI Uses/Month</li>
                                                )}
                                                {plan.features.store_deals_limit === 0 ? (
                                                    <li>Unlimited Store Searches</li>
                                                ) : (
                                                    <li>{plan.features.store_deals_limit} Store Searches/Month</li>
                                                )}
                                                <li>Priority Support</li>
                                                <li>No Ads</li>
                                            </ul>
                                        </div>

                                        <button 
                                            className={`upgrade-plan-btn ${upgrading && selectedPlan === plan.id ? 'upgrading' : ''}`}
                                            onClick={() => handleUpgrade(plan.id)}
                                            disabled={upgrading}
                                        >
                                            {upgrading && selectedPlan === plan.id ? (
                                                <>
                                                    <span className="upgrading-spinner"></span>
                                                    Processing...
                                                </>
                                            ) : (
                                                `Upgrade to ${plan.display_name}`
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Benefits section */}
                    <div className="upgrade-benefits">
                        <h4>Why Upgrade?</h4>
                        <div className="upgrade-benefits-grid">
                            <div className="upgrade-benefit-item">
                                <div className="upgrade-benefit-text">
                                    <h5>Smart Meal Planning</h5>
                                    <p>AI-powered meal suggestions based on your preferences</p>
                                </div>
                            </div>
                            <div className="upgrade-benefit-item">
                                <div className="upgrade-benefit-text">
                                    <h5>Recipe Collections</h5>
                                    <p>Access thousands of curated recipes and cooking guides</p>
                                </div>
                            </div>
                            <div className="upgrade-benefit-item">
                                <div className="upgrade-benefit-text">
                                    <h5>Unlimited Savings</h5>
                                    <p>Find the best deals across all stores without limits</p>
                                </div>
                            </div>
                            <div className="upgrade-benefit-item">
                                <div className="upgrade-benefit-text">
                                    <h5>Personalized Experience</h5>
                                    <p>Custom recommendations tailored to your shopping habits</p>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    );
}

export default UpgradeModal; 